(ns manage.adset-decision-tree-page.view.select-ad-variants-modal
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Checkbox$default" :as Checkbox]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium"
             :refer [DataGridPremium GRID_CHECKBOX_SELECTION_COL_DEF useGridApiRef]]
            [cljs-bean.core :refer [->clj]]
            [clojure.set :as set]
            [clojure.string :as str]
            [common.modal.view :refer [defmodal modal-v2]]
            [common.util :as util]
            [common.widgets.data-grid :as dg]
            [manage.ad-variant.data :as ad]
            [manage.ad-variant.hooks :as ah]
            [manage.ad-variant.page.columns :as ad-variant-columns]
            [manage.ad-variant.page.table-util :refer [ad-variant-table-filters]]
            [manage.ad-variant.queries :as aq]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.view.common
             :refer [chip upgrade-ad-variants-button]]
            [manage.adset-decision-tree-page.view.hook :as hook]
            [manage.adset-decision-tree-page.view.table-util :as tu]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui render-revision-cell [{:keys [value]
                              {:keys [update-fn]} :row}]
  ($ Stack {:direction "row"
            :alignItems "center"
            :gap 1}
     (when update-fn
       ($ chip {:color "primary"
                :label "new"}))
     value))

(def upgrade-column
  #js {:field "upgrade"
       :renderCell (fn [^js params]
                     (when-let [update-fn (:update-fn (.-row params))]
                       ($ upgrade-ad-variants-button {:on-upgrade update-fn})))
       ;; Hide the name on header row
       :renderHeader (fn [^js _params])
       :cellClassName "hover-action-cell"
       :sortable false
       :filterable false
       :resizeable false
       :groupable false
       :width 60})

(def ad-variant-base-fields
  [:ad-type
   :approval
   :content-id
   :revision
   :uuid
   :ad-variant-uuid
   :ad-formats
   :ad-variant-status
   :scheduling
   :dimensions])

(defhook use-dependent-row-ids
  [has-grouping row-id {:keys [children] row-node-type :type}]
  (let [is-group-row (= "group" row-node-type)]
    (uix/use-memo
     #(if (and has-grouping is-group-row) (set children) #{row-id})
     [children row-id has-grouping is-group-row])))

(defui selection-checkbox
  [{:keys [selected-ids on-change dependent-row-ids disabled]}]
  (let [on-change (uix/use-callback (fn [event] (on-change dependent-row-ids event))
                                    [on-change dependent-row-ids])
        all-dependent-checked (set/subset? dependent-row-ids selected-ids)]
    ($ Checkbox
       {:checked all-dependent-checked
        :indeterminate (and (not all-dependent-checked)
                            (boolean (seq (set/intersection selected-ids dependent-row-ids))))
        :onChange on-change
        :disabled disabled})))

(defui ad-variant-checkbox-cell
  [{:keys [selected-ids has-grouping on-change params]}]
  ($ selection-checkbox
     {:selected-ids selected-ids
      :on-change on-change
      :dependent-row-ids (use-dependent-row-ids has-grouping (:id params) (:rowNode params))}))

(defui ad-variant-checkbox-header
  [{:keys [selected-ids single-default-per-dimensions rows on-change]}]
  ($ selection-checkbox
     {:selected-ids selected-ids
      :on-change on-change
      :dependent-row-ids (uix/use-memo #(set (map :ad-variant-uuid rows)) [rows])
      :disabled single-default-per-dimensions}))

(defn add-upgrader
  [assigned-variants selected-rows set-selected-rows {:keys [ad-variant-uuid] :as row}]
  (let [ad-variant (get assigned-variants ad-variant-uuid)

        row-upgraded?
        (some (fn [{row-id :ad-variant-uuid
                    row-newer-revisions :newer-revisions}]
                (and (= row-id ad-variant-uuid) (empty? row-newer-revisions)))
              selected-rows)]
    (cond-> row
      (and (some? ad-variant)
           (sub/has-ready-newer-revisions? ad-variant)
           (not row-upgraded?))
      (assoc :update-fn
             (fn []
               (set-selected-rows (hook/upgrade-ad-variant selected-rows ad-variant)))))))

;; Use own implementation for checkbox selection, because MUI datagrid has
;; no support for selection with server side paginated tables and row grouping
(defhook use-ad-variant-checkbox-selection
  [{:keys [selected-ad-variants single-default-per-dimensions has-grouping rows ^js api-ref]}]
  (let [[selection set-selection] (uix/use-state selected-ad-variants)
        selected-ids (uix/use-memo #(set (map :ad-variant-uuid selection)) [selection])

        get-footer-row-selected-text (uix/use-callback
                                      (fn [_]
                                        (when-not (empty? selection)
                                          (let [selection-count (count selection)]
                                            (str selection-count " Ad variant"
                                                 (when (> selection-count 1) "s")
                                                 " selected"))))
                                      [selection])

        existing-selection-lookup (uix/use-memo
                                   #(mc/index-by :ad-variant-uuid selected-ad-variants)
                                   [selected-ad-variants])

        ad-variant-row-lookup (uix/use-memo
                               #(mc/index-by
                                 :ad-variant-uuid
                                 (fn [ad-variant]
                                   (select-keys ad-variant ad-variant-base-fields))
                                 rows)
                               [rows])

        resolve-row-id (uix/use-callback
                        (fn [row-id]
                          (get ad-variant-row-lookup row-id))
                        [ad-variant-row-lookup])

        row->relevant-dimensions (fn [row]
                                   (-> (:dimensions row)
                                       (select-keys [:width :height :duration])
                                       util/filter-nils-map))

        remove-conflicting-dimensions
        (uix/use-callback
         (fn [selection new-rows]
           (let [new-dimensions (set (map row->relevant-dimensions new-rows))]
             (->> selection
                  (remove #(contains? new-dimensions (row->relevant-dimensions %)))
                  vec)))
         [row->relevant-dimensions])

        dimensions (ah/use-dimensions)

        not-selected-dimensions
        (uix/use-memo
         (fn []
           (let [selected-dimensions (set (map row->relevant-dimensions selection))]
             (set/difference dimensions selected-dimensions)))
         [selection dimensions row->relevant-dimensions])

        on-change (uix/use-callback
                   (fn [dependent-row-ids event]
                     (if (-> event .-target .-checked)
                       (let [rows-to-add (->> (set/difference dependent-row-ids selected-ids)
                                              (mapv resolve-row-id))]
                         (set-selection
                          (fn [selection]
                            (-> selection
                                (cond-> single-default-per-dimensions (remove-conflicting-dimensions rows-to-add))
                                (concat rows-to-add)
                                vec))))
                       (set-selection
                        (fn [selection]
                          (vec (remove #(contains? dependent-row-ids (:ad-variant-uuid %))
                                       selection))))))
                   [resolve-row-id remove-conflicting-dimensions single-default-per-dimensions selected-ids])

        rows (uix/use-memo
              (fn []
                (->> rows
                     (map #(add-upgrader existing-selection-lookup
                                         selection
                                         set-selection
                                         %))
                     to-array))
              [rows existing-selection-lookup selection])

        get-row-classname (uix/use-callback
                           (fn [^js params]
                             (let [row-node (.. api-ref -current (getRowNode (.-id params)))
                                   row-ids (use-dependent-row-ids has-grouping
                                                                  (.-id params)
                                                                  (->clj row-node))]
                               (when (set/subset? row-ids selected-ids)
                                 "Mui-selected")))
                           [selected-ids api-ref has-grouping])]
    {:column (uix/use-memo
              #(util/spread-obj
                GRID_CHECKBOX_SELECTION_COL_DEF
                #js {:renderCell (fn [^js params]
                                   ($ ad-variant-checkbox-cell
                                      {:selected-ids selected-ids
                                       :has-grouping has-grouping
                                       :on-change on-change
                                       :params (->clj params)}))
                     :renderHeader (fn [_]
                                     ($ ad-variant-checkbox-header
                                        {:selected-ids selected-ids
                                         :single-default-per-dimensions single-default-per-dimensions
                                         :rows rows
                                         :on-change on-change}))})
              [on-change selected-ids single-default-per-dimensions has-grouping rows])
     :selection selection
     :not-selected-dimensions not-selected-dimensions
     :table-props {:rows rows
                   ;; There should be something in the rowSelectionModel otherwise MUI hides
                   ;; the :footerRowSelected text
                   :rowSelectionModel #js [""]
                   :getRowClassName get-row-classname
                   :keepNonExistentRowsSelected true
                   :localeText #js {:footerRowSelected get-footer-row-selected-text}
                   :checkboxSelection true}}))

(defn get-row-id [row] (:ad-variant-uuid row))

(defn status-aggregator [^js params]
  (.-values params))

(def table-sx (clj->js (merge tu/modal-table-default-sx
                              tu/hidden-aggregation-column-header-label-sx
                              dg/hover-action-sx)))

(def Toolbar
  (uix/as-react
   (fn [_]
     ($ dg/grid-toolbar
        ($ ad-variant-table-filters)))))

(defhook use-modal-controls
  [{:keys [set-ad-variants single-default-per-dimensions selected-ad-variants disable-grouping]}]
  (let [api-ref (useGridApiRef)
        {:keys [rowGroupingModel] :as row-grouping-props}
        (ah/use-ad-variant-grouping-props
         {:groups-opened-by-default false
          :api-ref api-ref
          :disable-grouping disable-grouping})

        url-context (use-subscribe [:url-context])
        table-query-fn (uix/use-callback
                        (fn [table-models]
                          (aq/ad-variants-by-content-id-query
                           url-context
                           {:field->sort-field ad-variant-columns/field->sort-field
                            :field->column ad-variant-columns/field->column
                            :field->resolver ad-variant-columns/field->resolver
                            :quick-filter {:field (ad-variant-columns/field->column
                                                   (.-field ad-variant-columns/identifier-column))}}
                           table-models))
                        [url-context])
        {:keys [rows] :as server-table-props}
        (dg/use-server-table-props
         {:row-grouping-model rowGroupingModel
          :initial-pagination-model #js {:page 0 :pageSize 8}
          :initial-filter-model
          (clj->js {:items [{:id "status"
                             :field "status"
                             :operator "isAnyOf"
                             :value (vec (remove #{"archived"} (keys ad/status)))}]})
          :table-query-fn table-query-fn})

        {selection-column :column
         selection-table-props :table-props
         selection :selection
         not-selected-dimensions :not-selected-dimensions}
        (use-ad-variant-checkbox-selection {:selected-ad-variants selected-ad-variants
                                            :single-default-per-dimensions single-default-per-dimensions
                                            :has-grouping (seq rowGroupingModel)
                                            :rows rows
                                            :api-ref api-ref})

        has-product-selector? (ad/has-product-selector? rows)

        columns (uix/use-memo
                 (fn []
                   (->> [selection-column
                         ad-variant-columns/identifier-column
                         (ad-variant-columns/get-ad-type-column {:show-scheduling true})
                         (when has-product-selector?
                           ad-variant-columns/product-selector-column)
                         (util/merge-objects ad-variant-columns/revision-id-column
                                             #js {:renderCell
                                                  (fn [^js params]
                                                    ($ render-revision-cell (->clj params)))})
                         ad-variant-columns/simple-status-column
                         upgrade-column]
                        (remove nil?)
                        into-array))
                 [selection-column has-product-selector?])]
    {:table-props
     (merge
      server-table-props
      row-grouping-props
      selection-table-props
      {;; the sorting happens on client except for content id, so disable server mode
       :sortingMode nil
       :initialState
       (util/merge-objects
        (clj->js {:aggregation {:model {(.-field ad-variant-columns/simple-status-column)
                                        "status-aggregator"}}})
        (:initialState row-grouping-props))
       :columns columns
       :columnHeaderHeight 50
       :getRowId get-row-id
       :disableAggregation false
       :disableColumnMenu true
       :disableRowSelectionOnClick true
       :keepNonExistentRowsSelected true
       :aggregationFunctions #js {:status-aggregator #js {:apply status-aggregator}}
       :getAggregationPosition dg/hide-root-group-row
       :slots #js {:toolbar Toolbar}
       :slotProps #js {:baseTextField dg/grid-toolbar-outlined-input}
       :localeText (util/merge-objects
                    #js {:toolbarQuickFilterPlaceholder "Search in identifier..."}
                    (:localeText selection-table-props))
       :noWrap true
       :sx table-sx})
     :not-selected-dimensions not-selected-dimensions
     :handle-done (fn [] (set-ad-variants (vec selection)))}))

(defui footer [{:keys [handle-done disabled]}]
  ($ Stack
     {:direction "row"
      :spacing 1
      :sx #js {:alignItems "right"}}
     ($ Button
        {:variant "outlined"
         :onClick #(rf/dispatch [:modal/close])}
        "Cancel")
     ($ Button
        {:variant "contained"
         :disabled disabled
         :onClick (fn [_]
                    (handle-done)
                    (rf/dispatch [:modal/close]))}
        "Done")))

(defmodal select-ad-variants-modal :decision-tree/select-ad-variants-modal
  [{:keys [single-default-per-dimensions set-ad-variants] :as props}]
  (let [{:keys [table-props handle-done not-selected-dimensions]} (use-modal-controls props)]
    ($ modal-v2
       {:title (if single-default-per-dimensions
                 "Select default ad variant(s)"
                 "Select ad variant(s)")
        :modifier :info
        :footer ($ footer
                   {:handle-done handle-done
                    :disabled (and single-default-per-dimensions (seq not-selected-dimensions))})
        :dialog-content-props {:sx #js {:display "flex"
                                        :flexDirection "column"
                                        :overflow "hidden"}}}
       (when single-default-per-dimensions
         ($ Alert {:severity (if (seq not-selected-dimensions) :warning :info)}
            ($ Stack {:gap 1}
               ($ Typography {:sx #js {:whiteSpace "pre-line"}}
                  "Please select one ad variant per unique dimensions.
                   Selecting a variant will automatically deselect other variants with the same dimensions.")
               (when (seq not-selected-dimensions)
                 ($ :p "Missing selections: "
                    (str/join ", "
                              (map #(ad/dimensions+duration {:dimensions %})
                                   not-selected-dimensions)))))))
       ($ DataGridPremium table-props))))
